import React, { useEffect, useRef, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface VideoGreetingModalProps {
  isOpen: boolean;
  onClose: () => void;
  videoUrl: string | null;
}

const VideoGreetingModal: React.FC<VideoGreetingModalProps> = ({ isOpen, onClose, videoUrl }) => {
  const { userData } = useAuth();
  const { t } = useTranslation();
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    if (isOpen && videoRef.current && videoUrl) {
      setIsLoading(true);
      setHasError(false);
      videoRef.current.load();
    }
  }, [isOpen, videoUrl]);

  const handleVideoLoaded = () => {
    setIsLoading(false);
    if (videoRef.current) {
      videoRef.current.play().catch((error) => {
        console.error('Error playing video:', error);
        setHasError(true);
      });
    }
  };

  const handleVideoError = () => {
    setIsLoading(false);
    setHasError(true);
  };

  const handleVideoEnded = () => {
    if (videoRef.current) {
      const video = videoRef.current;
      video.pause();
      video.currentTime = video.duration;
    }
  };

  if (!isOpen || !videoUrl) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
      <div className="relative w-full max-w-md animate-fadeIn">
        <div className="overflow-hidden rounded-2xl bg-white shadow-2xl">
          <div className="relative aspect-square w-full overflow-hidden bg-gray-100">
            {isLoading && (
              <div className="absolute inset-0 flex items-center justify-center">
                <Loader2 className="h-10 w-10 animate-spin text-gray-600" />
              </div>
            )}
            
            {hasError ? (
              <div className="absolute inset-0 flex items-center justify-center bg-gray-100">
                <div className="text-center px-6">
                  <p className="mb-4 text-gray-700">{t('errors.video_load_error')}</p>
                  <button
                    onClick={onClose}
                    className="rounded-lg bg-blue-600 px-4 py-2 text-white hover:bg-blue-700 transition-colors"
                  >
                    {t('actions.continue')}
                  </button>
                </div>
              </div>
            ) : (
              <video
                ref={videoRef}
                className="h-full w-full object-cover"
                onLoadedData={handleVideoLoaded}
                onError={handleVideoError}
                onEnded={handleVideoEnded}
                playsInline
                muted
              >
                <source src={videoUrl} type="video/mp4" />
                <source src={videoUrl} type="video/webm" />
                Tu navegador no soporta videos HTML5.
              </video>
            )}
          </div>
          
          <div className="bg-white px-6 py-8 text-center">
            <h2 className="text-2xl font-semibold text-gray-800">
              {t('common.hello')} {userData?.name || t('common.user')}!
            </h2>
            <button
              onClick={onClose}
              className="mt-6 rounded-lg bg-blue-600 px-8 py-3 text-white font-medium hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-all transform hover:scale-105"
            >
              {t('actions.continue')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VideoGreetingModal;